/**
 * Custom Scroll to Top Button
 * 
 * Handles the custom scroll-to-top button functionality:
 * - Shows/hides button based on scroll position
 * - Smooth scrolling to top when clicked
 * - Respects user's motion preferences
 */

class CustomScrollToTop {
  constructor() {
    this.button = document.getElementById('CustomScrollToTopButton');
    this.threshold = parseInt(this.button?.dataset.threshold) || 300;
    this.isVisible = false;
    this.ticking = false;
    this.isScrolling = false;
    
    if (!this.button) return;
    
    this.init();
  }

  init() {
    // Bind events
    this.button.addEventListener('click', this.scrollToTop.bind(this));
    window.addEventListener('scroll', this.handleScroll.bind(this), { passive: true });
    
    // Initial check
    this.updateButtonVisibility();
  }

  handleScroll() {
    // Don't update visibility during programmatic scrolling
    if (this.isScrolling) return;
    
    if (!this.ticking) {
      requestAnimationFrame(() => {
        this.updateButtonVisibility();
        this.ticking = false;
      });
      this.ticking = true;
    }
  }

  updateButtonVisibility() {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const shouldShow = scrollTop > this.threshold;
    
    if (shouldShow !== this.isVisible) {
      this.isVisible = shouldShow;
      
      if (shouldShow) {
        this.showButton();
      } else {
        this.hideButton();
      }
    }
  }

  showButton() {
    this.button.hidden = false;
    // Use setTimeout to ensure the element is rendered before adding the class
    setTimeout(() => {
      this.button.classList.add('show');
    }, 10);
  }

  hideButton() {
    this.button.classList.remove('show');
    // Wait for transition to complete before hiding
    setTimeout(() => {
      if (!this.button.classList.contains('show')) {
        this.button.hidden = true;
      }
    }, 300);
  }

  scrollToTop() {
    // Prevent scroll event handling during programmatic scroll
    this.isScrolling = true;
    
    // Check if user prefers reduced motion
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    
    if (prefersReducedMotion) {
      // Instant scroll for users who prefer reduced motion
      window.scrollTo(0, 0);
      this.isScrolling = false;
    } else {
      // Smooth scroll for others
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: 'smooth'
      });
      
      // Monitor scroll position to detect when smooth scroll is complete
      this.waitForScrollComplete();
    }
  }

  waitForScrollComplete() {
    const checkScrollPosition = () => {
      const currentScrollTop = window.pageYOffset || document.documentElement.scrollTop;
      
      if (currentScrollTop <= 1) {
        // Scroll is complete (allowing for small rounding errors)
        this.isScrolling = false;
      } else {
        // Continue checking
        requestAnimationFrame(checkScrollPosition);
      }
    };
    
    // Start checking after a small delay
    setTimeout(checkScrollPosition, 50);
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new CustomScrollToTop();
  });
} else {
  new CustomScrollToTop();
}
